import { FormApi } from "final-form";
import _ from "lodash";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { Modal } from "src/ui/shared/Modal/Modal";

import { useUpdateFileMetadataMutation, useUpdateFileRolloutMutation } from "@dashboard/files/api";
import { DialogProps, InputAdornment } from "@mui/material";
import CompareService from "@services/compareService/compareService";
import { PrimaryButton } from "@shared/CustomButton";
import { NumberField } from "@shared/form/NumberField";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { getErrorMessage } from "@shared/toasts/useQueryErrorToast";

import { TagManagementSection } from "./TagManagementSection";

type FormValues = {
    rolloutPercentage: number;
    tags: API.Tag[];
};

type Props = Omit<DialogProps, "onClose"> & {
    item?: API.File;
    onClose: (event?: object) => void;
};

const getFormDiff = (form: FormApi<FormValues>) => {
    const rolloutPercentageChanged = form.getFieldState("rolloutPercentage")?.dirty;
    const tagsChanged = new CompareService(
        _.keyBy(form.getState().initialValues?.tags, "key"),
        _.keyBy(form.getState().values?.tags, "key"),
    ).hasDifferences;
    const pristine = !rolloutPercentageChanged && !tagsChanged;

    return { pristine, rolloutPercentageChanged, tagsChanged };
};
const validate = (values: FormValues) => {
    const errors: Partial<Record<keyof FormValues, string>> = {};
    if (typeof values.rolloutPercentage !== "number") {
        errors.rolloutPercentage = i18next.t("modals:fileSettings.input.rolloutPercentage.validation.number");
    }
    if (values.rolloutPercentage < 0 || values.rolloutPercentage > 100) {
        errors.rolloutPercentage = i18next.t("modals:fileSettings.input.rolloutPercentage.validation.range");
    }
    return errors;
};

export const FileSettingsDialog = (props: Props) => {
    const { t } = useTranslation(["modals", "common"]);

    const [updateFileMetadata, { isLoading: isUpdatingMetadata }] = useUpdateFileMetadataMutation();
    const [updateFileRollout, { isLoading: isUpdatingRollout }] = useUpdateFileRolloutMutation();
    const isSubmitting = isUpdatingMetadata || isUpdatingRollout;

    const onSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
        if (!props.item) {
            return;
        }

        const { rolloutPercentageChanged, tagsChanged } = getFormDiff(form);

        try {
            if (rolloutPercentageChanged) {
                await updateFileRollout({ id: props.item.id, rolloutPercentage: +values.rolloutPercentage }).unwrap();
            }
            if (tagsChanged) {
                await updateFileMetadata({
                    fileId: props.item.id,
                    metadata: { ...props.item.metadata?.user, tags: values.tags },
                }).unwrap();
            }
            props.onClose();
            showSuccessToast(t("modals:fileSettings.toast.fileSettingsUpdatedSuccess"));
        } catch (error) {
            showErrorToast(getErrorMessage(error, t("modals:fileSettings.toast.fileSettingsUpdatedError")));
        }
    };
    const onKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>, form: FormApi<FormValues>) => {
        if (e.key === "Enter" && !getFormDiff(form).pristine && !isSubmitting) {
            await form.submit();
        }
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={validate}
            initialValues={{
                rolloutPercentage: props.item?.rolloutPercentage,
                tags: props.item?.metadata?.user?.tags || [],
            }}
            render={({ handleSubmit, form, values }) => (
                <form onSubmit={handleSubmit}>
                    <Modal
                        title={t("modals:fileSettings.title")}
                        {...props}
                        onKeyDown={(e) => onKeyDown(e, form)}
                        endButton={
                            <PrimaryButton onClick={form.submit} disabled={getFormDiff(form).pristine || isSubmitting}>
                                {t("common:button.save")}
                            </PrimaryButton>
                        }
                    >
                        <TagManagementSection tags={values?.tags || []} onChange={(data) => form.change("tags", data)} />
                        <NumberField
                            name="rolloutPercentage"
                            label={t("modals:fileSettings.input.rolloutPercentage.caption")}
                            fullWidth
                            required
                            onChange={(e) => {
                                form.change("rolloutPercentage", Number(e.target.value));
                            }}
                            slotProps={{
                                input: {
                                    startAdornment: <InputAdornment position="start">%</InputAdornment>,
                                },
                            }}
                        />
                    </Modal>
                </form>
            )}
        />
    );
};
